// EXEC-BRIEF V2 — whole-business morning brief composition (deterministic, zero-LLM).
//
// Founder spec: "what does a C-level exec care for when they wake up: WHAT HAPPENED TO MY
// ENTIRE BUSINESS." Six sections, all composed over EXISTING read models — no new engines.
// This module is PURE: it takes normalized six-section inputs plus a binding registry and
// emits BriefSpecSection[]. The api adapter maps live read models into the input shape.
//
// Laws honored here:
//   - Binding identity: every rendered number resolves from the binding registry by id.
//     A referenced binding that is absent renders as a labeled GAP — never an invented number.
//   - RENDER-OPEN: data present renders WITH a posture label; genuinely-absent renders a
//     named gap. Sections render only when they carry content.
//   - ASK-HELIX-CONNECTED / STORY-SURFACE: every item declares a drill-down (inline detail
//     card or a mapped Ask Helix question), and the coverage badge deep-links to approvals.

import type {
  BriefBindingSourceReadModel,
  BriefSpecAskHelixLink,
  BriefSpecBinding,
  BriefSpecItem,
  BriefSpecItemDrillDown,
  BriefSpecSection,
  BriefSpecItemProvenance,
  BriefWatchGeneratedItem,
} from './briefs-agent';
import {
  EMPTY_CANVAS_SNAPSHOT,
  diffCanvasSnapshots,
  type CanvasSnapshot,
  type CanvasTransition,
} from './canvas-transition';

export const EXEC_BRIEF_V2_SECTION_KINDS = [
  'the_number',
  'money_in_motion',
  'drifts',
  'issues',
  'needs_you',
  'what_changed',
] as const;

export type ExecBriefV2SectionKind = (typeof EXEC_BRIEF_V2_SECTION_KINDS)[number];

export const EXEC_BRIEF_V2_SECTION_TITLES: Record<ExecBriefV2SectionKind, string> = {
  the_number: 'The number',
  money_in_motion: 'Money in motion',
  drifts: 'Drifts',
  issues: 'Issues',
  needs_you: 'Needs you',
  what_changed: 'What changed',
};

// Closed render-posture enum (RENDER-OPEN law). Drives the RAG mapping and the posture chip.
export type ExecBriefV2Posture =
  | 'certified'
  | 'approved_source'
  | 'modeled'
  | 'pending_owner_validation'
  | 'planning_view'
  | 'pre_receipt'
  | 'gap';

export type ExecBriefV2Rag = 'green' | 'amber' | 'red';

const POSTURE_LABELS: Record<ExecBriefV2Posture, string> = {
  certified: 'Certified',
  approved_source: 'Approved source',
  modeled: 'Modeled',
  pending_owner_validation: 'Pending owner validation',
  planning_view: 'Planning view',
  pre_receipt: 'Pre-receipt',
  gap: 'Gap',
};

export function execBriefPostureLabel(posture: ExecBriefV2Posture): string {
  return POSTURE_LABELS[posture] ?? 'Gap';
}

// A registry binding, mirroring BriefSpecBinding but self-contained for the pure input.
export type ExecBriefV2Binding = {
  bindingId: string;
  label: string;
  valueLabel: string | null;
  numericValue?: number | null;
  unit?: string | null;
  sourceReadModel: BriefBindingSourceReadModel;
  sourcePath: string;
  sourceId: string;
  asOf?: string | null;
};

export type ExecBriefMomentum = 'up' | 'down' | 'flat';

// (1) THE NUMBER — revenue / net-revenue / OI / EBITDA pulse + momentum direction.
export type ExecBriefNumberPulse = {
  metricId: string;
  label: string;
  actualBindingId: string;
  planVarianceLabel?: string | null;
  momentum: ExecBriefMomentum;
  momentumLabel?: string | null;
  rag: ExecBriefV2Rag;
  detail?: string | null;
  askQuestion?: string | null;
};

// (2) MONEY IN MOTION — priced opportunities + value at risk, postures visible.
export type ExecBriefMoneyItem = {
  itemId: string;
  label: string;
  kind: 'opportunity' | 'value_at_risk';
  valueBindingId: string;
  posture: ExecBriefV2Posture;
  pctOfGapLabel?: string | null;
  detail?: string | null;
  askQuestion?: string | null;
};

// (3) DRIFTS — off-curve commitments + module warning-band reds, ranked by $ gap.
export type ExecBriefDriftItem = {
  itemId: string;
  label: string;
  source: 'commitment' | 'warning_band';
  gapBindingId: string;
  verdictLabel: string;
  posture: ExecBriefV2Posture;
  rag: ExecBriefV2Rag;
  owner?: string | null;
  detail?: string | null;
  askQuestion?: string | null;
  /**
   * ANSWERABILITY GATE (render-open law: never suggest a question we cannot
   * answer). When explicitly `false`, no governed source is bound for this watch
   * item's scope, so `askQuestion` must NOT be offered as a clickable dead-end —
   * the composer renders a gap-status chip instead. Undefined/true = answerable.
   */
  answerable?: boolean;
  /** Human label for the missing source when `answerable === false`. */
  unanswerableStatus?: string | null;
};

// (4) ISSUES — blocked items + named evidence gaps costing decisions.
export type ExecBriefIssueItem = {
  itemId: string;
  label: string;
  kind: 'blocked' | 'evidence_gap';
  whatIsNeeded: string;
  costLabel?: string | null;
  detail?: string | null;
  askQuestion?: string | null;
};

// (5) NEEDS YOU — the docket: pending approvals + AUTOPILOT drafts + cost-of-delay.
export type ExecBriefDocketItem = {
  itemId: string;
  label: string;
  kind: 'pending_approval' | 'autopilot_draft' | 'cost_of_delay';
  valueBindingId?: string | null;
  recommended?: boolean;
  actionId?: string | null;
  dueLabel?: string | null;
  detail?: string | null;
  askQuestion?: string | null;
};

// (6) WHAT CHANGED — movement since last visit, with receipts.
export type ExecBriefMovementDirection = 'up' | 'down' | 'flat' | 'new' | 'resolved';

export type ExecBriefMovementItem = {
  itemId: string;
  label: string;
  direction: ExecBriefMovementDirection;
  deltaBindingId?: string | null;
  deltaLabel?: string | null;
  rag: ExecBriefV2Rag;
  detail?: string | null;
  askQuestion?: string | null;
};

export type ExecBriefV2Coverage = {
  watching: number;
  total: number;
  approveQuestion?: string | null;
};

export type ExecBriefV2RoleLens = 'ceo' | 'cfo' | 'coo' | 'chro' | 'neutral';

export type ExecBriefV2Input = {
  roleLens?: ExecBriefV2RoleLens;
  coverage: ExecBriefV2Coverage;
  theNumber: ExecBriefNumberPulse[];
  moneyInMotion: ExecBriefMoneyItem[];
  drifts: ExecBriefDriftItem[];
  issues: ExecBriefIssueItem[];
  needsYou: ExecBriefDocketItem[];
  whatChanged: ExecBriefMovementItem[];
  watchItems?: readonly BriefWatchGeneratedItem[] | null;
  bindings: ExecBriefV2Binding[];
  /**
   * Closed Delta Brief mode. This is deliberately an optional mode inside the
   * existing canonical composer; it is not a second composer or a router.
   */
  deltaFirst?: ExecBriefV2DeltaFirstInput | null;
};

export type ExecBriefV2DeltaSourceItem = {
  sourceItemId: string;
  commitmentId: string;
  ownerUserId: string;
  label: string;
  detail: string;
  posture: ExecBriefV2Posture;
  rag: ExecBriefV2Rag;
  /** Only source-emitted admitted commitment-impact bindings may price a group. */
  impactBindingId: string | null;
  evidenceRefIds: string[];
  formulaId?: string | null;
  formulaVersion?: string | null;
  sourceClass: string;
  sourcePath: string;
  sourceId: string;
  askQuestion: string;
};

export type ExecBriefV2DeltaFirstInput = {
  scope: {
    tenantId: string;
    strategyId: string;
    ownerUserId: string;
  };
  priorSnapshot: CanvasSnapshot | null;
  currentSnapshot: CanvasSnapshot;
  sourceItems: readonly ExecBriefV2DeltaSourceItem[];
};

export type ExecBriefV2CoverageBadge = {
  label: string;
  watching: number;
  total: number;
  askHelix?: BriefSpecAskHelixLink | null;
};

export type ExecBriefV2Composed = {
  sections: BriefSpecSection[];
  coverageBadge: ExecBriefV2CoverageBadge;
  bindings: BriefSpecBinding[];
  ledeRag: ExecBriefV2Rag;
  ledeReason: string | null;
};

const POSTURE_RAG: Record<ExecBriefV2Posture, ExecBriefV2Rag> = {
  certified: 'green',
  approved_source: 'green',
  modeled: 'amber',
  pending_owner_validation: 'amber',
  planning_view: 'amber',
  pre_receipt: 'amber',
  gap: 'red',
};

function ragFromPosture(posture: ExecBriefV2Posture): ExecBriefV2Rag {
  return POSTURE_RAG[posture] ?? 'amber';
}

function ragTag(rag: ExecBriefV2Rag): string {
  return rag;
}

// Order lenses can reorder: CFO edition leads money-first. Default is canonical order.
function sectionOrder(roleLens: ExecBriefV2RoleLens): ExecBriefV2SectionKind[] {
  if (roleLens === 'cfo') {
    return ['the_number', 'money_in_motion', 'drifts', 'needs_you', 'issues', 'what_changed'];
  }
  return [...EXEC_BRIEF_V2_SECTION_KINDS];
}

function resolveBinding(
  bindings: Map<string, ExecBriefV2Binding>,
  bindingId: string | null | undefined
): ExecBriefV2Binding | null {
  if (!bindingId) return null;
  return bindings.get(bindingId) ?? null;
}

// Resolve a value strictly from the binding registry. A missing/unpriced binding degrades
// to a labeled gap — the number is never invented.
function resolveValueLabel(
  bindings: Map<string, ExecBriefV2Binding>,
  bindingId: string | null | undefined
): { valueLabel: string | null; bindingIds: string[]; gap: boolean } {
  const binding = resolveBinding(bindings, bindingId);
  if (!binding || binding.valueLabel == null || binding.valueLabel.trim() === '') {
    return { valueLabel: null, bindingIds: bindingId ? [bindingId] : [], gap: true };
  }
  return { valueLabel: binding.valueLabel, bindingIds: [binding.bindingId], gap: false };
}

function askLink(question: string | null | undefined, label = 'Ask about this'): BriefSpecAskHelixLink | null {
  const text = (question ?? '').trim();
  if (!text) return null;
  return { label, question: text };
}

function inlineDrill(question?: string | null): BriefSpecItemDrillDown {
  return { mode: 'inline', question: (question ?? '').trim() || null };
}

function askDrill(question?: string | null): BriefSpecItemDrillDown {
  return { mode: 'ask', question: (question ?? '').trim() || null };
}

function momentumArrow(momentum: ExecBriefMomentum): string {
  if (momentum === 'up') return '▲';
  if (momentum === 'down') return '▼';
  return '▬';
}

function directionArrow(direction: ExecBriefMovementDirection): string {
  switch (direction) {
    case 'up':
      return '▲';
    case 'down':
      return '▼';
    case 'new':
      return '＋';
    case 'resolved':
      return '✓';
    default:
      return '▬';
  }
}

function section(
  kind: ExecBriefV2SectionKind,
  items: BriefSpecItem[],
  body: string,
  emptyState: string | null,
  bindingIds: string[]
): BriefSpecSection | null {
  if (items.length === 0) return null;
  return {
    sectionId: kind,
    kind,
    title: EXEC_BRIEF_V2_SECTION_TITLES[kind],
    body,
    evidenceRefIds: bindingIds,
    items,
    emptyState,
  };
}

function pushBinding(
  out: BriefSpecBinding[],
  seen: Set<string>,
  bindings: Map<string, ExecBriefV2Binding>,
  bindingIds: string[]
): void {
  for (const id of bindingIds) {
    if (seen.has(id)) continue;
    const binding = bindings.get(id);
    if (!binding) continue;
    seen.add(id);
    out.push({
      bindingId: binding.bindingId,
      label: binding.label,
      valueLabel: binding.valueLabel,
      numericValue: binding.numericValue ?? null,
      unit: binding.unit ?? null,
      sourceReadModel: binding.sourceReadModel,
      sourcePath: binding.sourcePath,
      sourceId: binding.sourceId,
      asOf: binding.asOf ?? null,
    });
  }
}

export function composeExecBriefV2(input: ExecBriefV2Input): ExecBriefV2Composed {
  const roleLens: ExecBriefV2RoleLens = input.roleLens ?? 'neutral';
  const bindings = new Map<string, ExecBriefV2Binding>();
  for (const binding of input.bindings) bindings.set(binding.bindingId, binding);

  if (input.deltaFirst) {
    return composeDeltaFirstExecBrief(input, bindings);
  }

  const referencedBindings: BriefSpecBinding[] = [];
  const seenBindings = new Set<string>();

  const byKind = new Map<ExecBriefV2SectionKind, BriefSpecSection>();

  // (1) THE NUMBER ---------------------------------------------------------------------
  {
    const items: BriefSpecItem[] = [];
    const sectionBindingIds: string[] = [];
    for (const pulse of input.theNumber) {
      const resolved = resolveValueLabel(bindings, pulse.actualBindingId);
      const arrow = momentumArrow(pulse.momentum);
      const varianceText = (pulse.planVarianceLabel ?? '').trim();
      const momentumText = (pulse.momentumLabel ?? '').trim();
      const postureLabel = resolved.gap ? 'Gap — not yet bound' : varianceText || 'vs plan';
      items.push({
        itemId: pulse.metricId,
        label: pulse.label,
        valueLabel: resolved.valueLabel,
        body: [varianceText, momentumText ? `${arrow} ${momentumText}` : ''].filter(Boolean).join(' · ') || null,
        detail: pulse.detail ?? null,
        posture: postureLabel,
        sourceLabel: momentumText ? `Momentum ${arrow}` : null,
        bindingIds: resolved.bindingIds,
        askHelix: askLink(pulse.askQuestion, 'How is this tracking?'),
        drillDown: askDrill(pulse.askQuestion),
        rag: resolved.gap ? 'red' : pulse.rag,
      });
      sectionBindingIds.push(...resolved.bindingIds);
      pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
    }
    const worst = worstRag(input.theNumber.map((p) => p.rag));
    const built = section(
      'the_number',
      items,
      pulseBody(input.theNumber, worst),
      null,
      dedupe(sectionBindingIds)
    );
    if (built) byKind.set('the_number', built);
  }

  // (2) MONEY IN MOTION ----------------------------------------------------------------
  {
    const items: BriefSpecItem[] = [];
    const sectionBindingIds: string[] = [];
    for (const money of input.moneyInMotion) {
      const resolved = resolveValueLabel(bindings, money.valueBindingId);
      const posture: ExecBriefV2Posture = resolved.gap ? 'gap' : money.posture;
      const pct = (money.pctOfGapLabel ?? '').trim();
      items.push({
        itemId: money.itemId,
        label: money.label,
        valueLabel: resolved.valueLabel,
        body: [money.kind === 'value_at_risk' ? 'Value at risk' : 'Priced opportunity', pct]
          .filter(Boolean)
          .join(' · '),
        detail: money.detail ?? null,
        posture: execBriefPostureLabel(posture),
        sourceLabel: ragTag(ragFromPosture(posture)),
        bindingIds: resolved.bindingIds,
        askHelix: askLink(money.askQuestion, 'Open the plan'),
        drillDown: askDrill(money.askQuestion),
        rag: money.kind === 'value_at_risk' && !resolved.gap ? 'amber' : ragFromPosture(posture),
      });
      sectionBindingIds.push(...resolved.bindingIds);
      pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
    }
    const built = section(
      'money_in_motion',
      items,
      moneyBody(input.moneyInMotion),
      null,
      dedupe(sectionBindingIds)
    );
    if (built) byKind.set('money_in_motion', built);
  }

  // (3) DRIFTS — ranked by $ gap (descending numeric where available) -----------------
  {
    const ranked = [...input.drifts].sort(
      (a, b) => driftGapValue(bindings, b) - driftGapValue(bindings, a)
    );
    const items: BriefSpecItem[] = [];
    const sectionBindingIds: string[] = [];
    for (const drift of ranked) {
      const resolved = resolveValueLabel(bindings, drift.gapBindingId);
      const posture: ExecBriefV2Posture = resolved.gap ? 'gap' : drift.posture;
      const ownerText = (drift.owner ?? '').trim();
      // ANSWERABILITY GATE (render-open): only offer the clickable "Are we on
      // track?" question when a governed source is bound for this watch item's
      // scope. When explicitly unanswerable, render a gap-status chip naming what
      // must be connected — never a clickable dead-end.
      const unanswerable = drift.answerable === false;
      const unanswerableStatus = unanswerable
        ? (drift.unanswerableStatus ?? '').trim() ||
          'Needs a governed source connected'
        : null;
      items.push({
        itemId: drift.itemId,
        label: drift.label,
        valueLabel: driftValueLabel(resolved.valueLabel),
        body: [drift.verdictLabel, ownerText ? `Owner: ${ownerText}` : ''].filter(Boolean).join(' · '),
        detail: drift.detail ?? drift.verdictLabel,
        posture: execBriefPostureLabel(posture),
        sourceLabel: ragTag(drift.rag),
        bindingIds: resolved.bindingIds,
        askHelix: unanswerable
          ? null
          : askLink(
              drift.askQuestion,
              drift.rag === 'green' ? 'Open the plan' : 'Why is this behind?',
            ),
        drillDown: unanswerable ? inlineDrill(null) : inlineDrill(drift.askQuestion),
        rag: drift.rag,
        ...(unanswerableStatus ? { unanswerableStatus } : {}),
      });
      sectionBindingIds.push(...resolved.bindingIds);
      pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
    }
    const built = section('drifts', items, driftBody(ranked), null, dedupe(sectionBindingIds));
    if (built) byKind.set('drifts', built);
  }

  // (4) ISSUES -------------------------------------------------------------------------
  {
    const items: BriefSpecItem[] = [];
    for (const issue of input.issues) {
      const costText = (issue.costLabel ?? '').trim();
      items.push({
        itemId: issue.itemId,
        label: issue.label,
        valueLabel: costText || null,
        body: [issue.kind === 'blocked' ? 'Blocked' : 'Evidence gap', costText]
          .filter(Boolean)
          .join(' · '),
        detail: `Needs: ${issue.whatIsNeeded}${issue.detail ? ` — ${issue.detail}` : ''}`,
        posture: issue.kind === 'blocked' ? 'Blocked' : 'Named gap',
        sourceLabel: 'red',
        askHelix: askLink(issue.askQuestion, 'What is blocking this?'),
        drillDown: inlineDrill(issue.askQuestion),
        rag: 'red',
      });
    }
    const built = section('issues', items, issueBody(input.issues), null, []);
    if (built) byKind.set('issues', built);
  }

  // (5) NEEDS YOU — the only to-do section --------------------------------------------
  {
    const items: BriefSpecItem[] = [];
    const sectionBindingIds: string[] = [];
    const hoistPendingApprovalState =
      commonDocketKind(input.needsYou) === 'pending_approval';
    input.needsYou.forEach((docket, index) => {
      const resolved = resolveValueLabel(bindings, docket.valueBindingId);
      const dueText = (docket.dueLabel ?? '').trim();
      const kindLabel =
        docket.kind === 'pending_approval'
          ? 'Pending approval'
          : docket.kind === 'autopilot_draft'
            ? 'Autopilot draft'
            : 'Cost of delay';
      items.push({
        itemId: `needs-you-${docket.itemId}`,
        label: docket.label,
        valueLabel: resolved.valueLabel,
        body: [
          hoistPendingApprovalState ? '' : kindLabel,
          dueText ? `Due ${compactDueLabel(dueText)}` : '',
        ]
          .filter(Boolean)
          .join(' · '),
        detail: docket.detail ?? null,
        posture: docket.kind === 'cost_of_delay' ? 'Clock running' : 'Awaiting sign-off',
        sourceLabel: docket.actionId ?? null,
        bindingIds: resolved.bindingIds,
        askHelix: askLink(
          docket.askQuestion,
          docket.kind === 'pending_approval'
            ? 'Walk me through the recovery'
            : 'Open the plan',
        ),
        drillDown: inlineDrill(docket.askQuestion),
        rag: docket.kind === 'cost_of_delay' ? 'red' : 'amber',
        recommended: Boolean(docket.recommended && index === 0),
        actionId: docket.actionId ?? null,
      });
      sectionBindingIds.push(...resolved.bindingIds);
      pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
    });
    const built = section('needs_you', items, docketBody(input.needsYou), null, dedupe(sectionBindingIds));
    if (built) byKind.set('needs_you', built);
  }

  // (6) WHAT CHANGED -------------------------------------------------------------------
  {
    const items: BriefSpecItem[] = [];
    const sectionBindingIds: string[] = [];
    const rankedWatches = [...(input.watchItems ?? [])].sort(
      (a, b) => watchValueAtStake(bindings, b) - watchValueAtStake(bindings, a)
    );
    for (const watch of rankedWatches) {
      const resolved = resolveValueLabel(bindings, watch.valueAtStakeBindingId);
      const rag = watch.rag ?? (resolved.gap ? 'red' : 'amber');
      const provenance = watchItemProvenance(watch);
      items.push({
        itemId: watch.itemId,
        label: watch.label,
        valueLabel: resolved.valueLabel,
        body: watchItemBody(watch),
        detail: watchItemDetail(watch),
        posture: resolved.gap ? 'Gap' : 'Watch fired',
        sourceLabel: ragTag(rag),
        bindingIds: resolved.bindingIds,
        askHelix: askLink(watch.suggestedQuestion, 'Ask about this'),
        drillDown: inlineDrill(watch.suggestedQuestion),
        rag,
        nextActionVerb: watch.nextActionVerb,
        provenance,
        actionId:
          watch.nextActionVerb === 'ask' ? null : `${watch.nextActionVerb}:${watch.itemId}`,
      });
      sectionBindingIds.push(...resolved.bindingIds);
      pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
    }
    for (const move of input.whatChanged) {
      const resolved = resolveValueLabel(bindings, move.deltaBindingId);
      const arrow = directionArrow(move.direction);
      const deltaText = resolved.valueLabel ?? ((move.deltaLabel ?? '').trim() || null);
      items.push({
        itemId: move.itemId,
        label: `${arrow} ${move.label}`,
        valueLabel: deltaText,
        body: move.deltaLabel ?? null,
        detail: move.detail ?? null,
        posture: movementPosture(move.direction),
        sourceLabel: ragTag(move.rag),
        bindingIds: resolved.bindingIds,
        askHelix: askLink(move.askQuestion, 'What changed?'),
        drillDown: askDrill(move.askQuestion),
        rag: move.rag,
      });
      sectionBindingIds.push(...resolved.bindingIds);
      pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
    }
    const built = section(
      'what_changed',
      items,
      movementBody([...rankedWatches, ...input.whatChanged]),
      null,
      dedupe(sectionBindingIds)
    );
    if (built) byKind.set('what_changed', built);
  }

  const sections: BriefSpecSection[] = [];
  for (const kind of sectionOrder(roleLens)) {
    const built = byKind.get(kind);
    if (built) sections.push(built);
  }

  return {
    sections,
    coverageBadge: buildCoverageBadge(input.coverage),
    bindings: referencedBindings,
    ledeRag: ledeRag(input, bindings),
    ledeReason: ledeReason(input, bindings),
  };
}

function composeDeltaFirstExecBrief(
  input: ExecBriefV2Input,
  bindings: Map<string, ExecBriefV2Binding>,
): ExecBriefV2Composed {
  const delta = input.deltaFirst;
  if (!delta) {
    return emptyDeltaFirstComposition(input.coverage);
  }

  const transition = diffCanvasSnapshots(
    delta.priorSnapshot ?? EMPTY_CANVAS_SNAPSHOT,
    delta.currentSnapshot,
  );
  const admittedItems = delta.sourceItems.filter(
    (item) => item.ownerUserId === delta.scope.ownerUserId,
  );
  const groups = groupDeltaItems(admittedItems, bindings, transition);
  const referencedBindings: BriefSpecBinding[] = [];
  const seenBindings = new Set<string>();
  const pricedItems = groups
    .filter((group) => group.impactBindingId !== null)
    .sort((left, right) =>
      deltaImpactValue(bindings, right.impactBindingId) -
        deltaImpactValue(bindings, left.impactBindingId) ||
      left.commitmentId.localeCompare(right.commitmentId),
    )
    .map((group) => deltaGroupToBriefItem(group, bindings, referencedBindings, seenBindings));
  const unpricedItems = groups
    .filter((group) => group.impactBindingId === null)
    .sort((left, right) => left.commitmentId.localeCompare(right.commitmentId))
    .map((group) => deltaGroupToBriefItem(group, bindings, referencedBindings, seenBindings));

  const sections: BriefSpecSection[] = [];
  if (pricedItems.length > 0) {
    sections.push({
      sectionId: 'delta-brief:priced-commitments',
      kind: 'what_changed',
      title: 'Commitments with priced impact',
      body: 'Source-provided commitment impact only; no Delta Brief score or roll-up.',
      evidenceRefIds: pricedItems.flatMap((item) => item.bindingIds ?? []),
      items: pricedItems,
      emptyState: null,
    });
  }
  if (unpricedItems.length > 0) {
    sections.push({
      sectionId: 'delta-brief:impact-not-yet-priced',
      kind: 'what_changed',
      title: 'Impact not yet priced',
      body: 'Source changes remain visible when no comparable governed impact binding exists.',
      evidenceRefIds: unpricedItems.flatMap((item) => item.bindingIds ?? []),
      items: unpricedItems,
      emptyState: null,
    });
  }
  if (sections.length === 0) {
    sections.push({
      sectionId: 'delta-brief:no-commitments',
      kind: 'what_changed',
      title: 'No commitments assigned',
      body: 'No governed commitment is currently bound to this officer.',
      evidenceRefIds: [],
      items: [],
      emptyState: 'No commitments assigned',
    });
  }

  const rags = [...pricedItems, ...unpricedItems].map((item) => item.rag ?? 'amber');
  return {
    sections,
    bindings: referencedBindings,
    coverageBadge: {
      label: `Watching ${groups.length} of ${groups.length} owned commitments`,
      watching: groups.length,
      total: groups.length,
      askHelix: null,
    },
    ledeRag: worstRag(rags),
    ledeReason:
      groups.length > 0
        ? 'Owner-scoped source changes are shown in commitment-impact order.'
        : 'No governed commitment is bound to this officer.',
  };
}

type DeltaCommitmentGroup = {
  commitmentId: string;
  impactBindingId: string | null;
  items: ExecBriefV2DeltaSourceItem[];
  transition: BriefSpecItemProvenance['transition'];
};

function groupDeltaItems(
  sourceItems: readonly ExecBriefV2DeltaSourceItem[],
  bindings: Map<string, ExecBriefV2Binding>,
  transition: CanvasTransition,
): DeltaCommitmentGroup[] {
  const groups = new Map<string, DeltaCommitmentGroup>();
  const seenSourceItems = new Set<string>();
  for (const item of sourceItems) {
    if (seenSourceItems.has(item.sourceItemId)) continue;
    seenSourceItems.add(item.sourceItemId);
    const existing = groups.get(item.commitmentId);
    const usableImpactBinding = isUsableDeltaImpactBinding(bindings, item.impactBindingId)
      ? item.impactBindingId
      : null;
    if (existing) {
      existing.items.push(item);
      if (existing.impactBindingId === null && usableImpactBinding !== null) {
        existing.impactBindingId = usableImpactBinding;
      }
      continue;
    }
    groups.set(item.commitmentId, {
      commitmentId: item.commitmentId,
      impactBindingId: usableImpactBinding,
      items: [item],
      transition: transitionForCommitment(item.commitmentId, transition),
    });
  }
  return [...groups.values()];
}

function deltaGroupToBriefItem(
  group: DeltaCommitmentGroup,
  bindings: Map<string, ExecBriefV2Binding>,
  referencedBindings: BriefSpecBinding[],
  seenBindings: Set<string>,
): BriefSpecItem {
  const lead = group.items[0];
  const resolved = resolveValueLabel(bindings, group.impactBindingId);
  pushBinding(referencedBindings, seenBindings, bindings, resolved.bindingIds);
  return {
    itemId: group.commitmentId,
    label: lead.label,
    valueLabel: resolved.valueLabel,
    body: group.items.map((item) => item.label).join(' · '),
    detail: group.items.map((item) => item.detail).join(' '),
    posture: execBriefPostureLabel(lead.posture),
    sourceLabel: lead.sourceClass,
    bindingIds: resolved.bindingIds,
    askHelix: askLink(lead.askQuestion, 'Why did this move?'),
    drillDown: askDrill(lead.askQuestion),
    rag: worstRag(group.items.map((item) => item.rag)),
    provenance: {
      label: `Why am I seeing this: ${group.items.map((item) => item.sourceItemId).join(', ')}.`,
      commitmentId: group.commitmentId,
      sourceItemIds: group.items.map((item) => item.sourceItemId),
      sourceIds: group.items.map((item) => item.sourceId),
      evidenceRefIds: dedupe(group.items.flatMap((item) => item.evidenceRefIds)),
      formulaId: lead.formulaId ?? null,
      formulaVersion: lead.formulaVersion ?? null,
      transition: group.transition,
      sourcePath: lead.sourcePath,
    },
  };
}

function isUsableDeltaImpactBinding(
  bindings: Map<string, ExecBriefV2Binding>,
  bindingId: string | null,
): bindingId is string {
  const binding = bindingId ? bindings.get(bindingId) : null;
  return Boolean(
    binding &&
      typeof binding.numericValue === 'number' &&
      Number.isFinite(binding.numericValue) &&
      binding.valueLabel?.trim(),
  );
}

function deltaImpactValue(
  bindings: Map<string, ExecBriefV2Binding>,
  bindingId: string | null,
): number {
  const value = bindingId ? bindings.get(bindingId)?.numericValue : null;
  return typeof value === 'number' && Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
}

function transitionForCommitment(
  commitmentId: string,
  transition: CanvasTransition,
): BriefSpecItemProvenance['transition'] {
  if (transition.updated.some((item) => item.componentId === commitmentId)) return 'updated';
  if (transition.pivoted.some((item) => item.componentId === commitmentId)) return 'pivoted';
  if (transition.entering.some((item) => item.component.componentId === commitmentId)) return 'entering';
  if (transition.exiting.includes(commitmentId)) return 'exiting';
  return 'current';
}

function emptyDeltaFirstComposition(coverage: ExecBriefV2Coverage): ExecBriefV2Composed {
  return {
    sections: [],
    bindings: [],
    coverageBadge: {
      label: `Watching ${coverage.watching} of ${coverage.total}`,
      watching: coverage.watching,
      total: coverage.total,
      askHelix: null,
    },
    ledeRag: 'red',
    ledeReason: 'Delta Brief mode was not supplied.',
  };
}

function buildCoverageBadge(coverage: ExecBriefV2Coverage): ExecBriefV2CoverageBadge {
  const total = Math.max(coverage.total, coverage.watching);
  const suffix =
    coverage.watching < total ? ' — approve more from your strategy' : ' — full portfolio instrumented';
  return {
    label: `Watching ${coverage.watching} of ${total} initiatives${suffix}`,
    watching: coverage.watching,
    total,
    askHelix:
      coverage.watching < total
        ? askLink(coverage.approveQuestion ?? 'What strategy commitments can I approve to monitor?', 'Approve more')
        : null,
  };
}

function worstRag(rags: ExecBriefV2Rag[]): ExecBriefV2Rag {
  if (rags.includes('red')) return 'red';
  if (rags.includes('amber')) return 'amber';
  return 'green';
}

function ledeRag(
  input: ExecBriefV2Input,
  bindings: Map<string, ExecBriefV2Binding>
): ExecBriefV2Rag {
  if (
    (input.watchItems ?? []).some(
      (item) => item.rag === 'red' || !bindings.has(item.valueAtStakeBindingId),
    )
  ) {
    return 'red';
  }
  if (input.drifts.some((d) => d.rag === 'red') || input.issues.length > 0) return 'red';
  if (
    (input.watchItems ?? []).length > 0 ||
    input.drifts.length > 0 ||
    input.needsYou.length > 0 ||
    input.moneyInMotion.some((m) => m.kind === 'value_at_risk')
  ) {
    return 'amber';
  }
  return worstRag(input.theNumber.map((p) => p.rag));
}

function ledeReason(
  input: ExecBriefV2Input,
  bindings: Map<string, ExecBriefV2Binding>
): string | null {
  const topDrift = [...input.drifts].sort((a, b) => (b.rag === 'red' ? 1 : 0) - (a.rag === 'red' ? 1 : 0))[0];
  if (topDrift) {
    return `${topDrift.label}: ${topDrift.verdictLabel}`;
  }
  const topWatch = [...(input.watchItems ?? [])].sort(
    (a, b) => watchValueAtStake(bindings, b) - watchValueAtStake(bindings, a)
  )[0];
  if (topWatch) {
    return `${topWatch.label}: ${topWatch.threshold.label}`;
  }
  if (input.issues[0]) {
    return `${input.issues[0].label} — needs ${input.issues[0].whatIsNeeded}`;
  }
  if (input.needsYou[0]) {
    return `${input.needsYou[0].label} awaits your decision`;
  }
  if (input.theNumber[0]) {
    return `${input.theNumber[0].label} ${input.theNumber[0].planVarianceLabel ?? 'is on plan'}`;
  }
  return null;
}

function movementPosture(direction: ExecBriefMovementDirection): string {
  switch (direction) {
    case 'new':
      return 'New since last visit';
    case 'resolved':
      return 'Resolved';
    case 'up':
      return 'Improved';
    case 'down':
      return 'Worsened';
    default:
      return 'Flat';
  }
}

function driftValueLabel(valueLabel: string | null): string | null {
  const value = (valueLabel ?? '').trim();
  if (!value) return null;
  return value.toLowerCase().includes('at risk') ? value : `${value} at risk`;
}

function driftGapValue(bindings: Map<string, ExecBriefV2Binding>, drift: ExecBriefDriftItem): number {
  const binding = bindings.get(drift.gapBindingId);
  return typeof binding?.numericValue === 'number' ? binding.numericValue : 0;
}

function dedupe(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

function pulseBody(pulses: ExecBriefNumberPulse[], worst: ExecBriefV2Rag): string {
  if (pulses.length === 0) return '';
  const lead = pulses[0];
  const state = worst === 'red' ? 'off plan' : worst === 'amber' ? 'watch vs plan' : 'on plan';
  return `${pulses.length} headline metric${pulses.length === 1 ? '' : 's'} — ${lead.label} ${state}.`;
}

function moneyBody(items: ExecBriefMoneyItem[]): string {
  const opps = items.filter((i) => i.kind === 'opportunity').length;
  const risk = items.filter((i) => i.kind === 'value_at_risk').length;
  const parts: string[] = [];
  if (opps) parts.push(`${opps} priced opportunit${opps === 1 ? 'y' : 'ies'}`);
  if (risk) parts.push(`${risk} value-at-risk position${risk === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

function driftBody(items: ExecBriefDriftItem[]): string {
  if (items.length === 0) return '';
  const reds = items.filter((i) => i.rag === 'red').length;
  const ambers = items.filter((i) => i.rag === 'amber').length;
  const offCurve = reds + ambers;
  if (offCurve === 0) {
    return `${items.length} commitment curve${items.length === 1 ? '' : 's'} on track — ranked by dollars at risk.`;
  }
  return `${items.length} commitment curve${items.length === 1 ? '' : 's'}, ${offCurve} off-curve${reds ? `, ${reds} red` : ''} — ranked by dollars at risk.`;
}

function issueBody(items: ExecBriefIssueItem[]): string {
  if (items.length === 0) return '';
  const blocked = items.filter((i) => i.kind === 'blocked').length;
  const gaps = items.filter((i) => i.kind === 'evidence_gap').length;
  const parts: string[] = [];
  if (blocked) parts.push(`${blocked} blocked`);
  if (gaps) parts.push(`${gaps} evidence gap${gaps === 1 ? '' : 's'}`);
  return `${parts.join(' · ')} costing decisions.`;
}

function docketBody(items: ExecBriefDocketItem[]): string {
  if (items.length === 0) return '';
  const commonKind = commonDocketKind(items);
  const state =
    commonKind === 'pending_approval'
      ? `${items.length} awaiting your sign-off`
      : `${items.length} decision${items.length === 1 ? '' : 's'} need your attention`;
  const dueRange = docketDueRange(items);
  return `${state}${dueRange ? ` · due ${dueRange}` : ''}.`;
}

function commonDocketKind(
  items: ExecBriefDocketItem[],
): ExecBriefDocketItem['kind'] | null {
  const first = items[0]?.kind;
  return first && items.every((item) => item.kind === first) ? first : null;
}

function compactDueLabel(value: string): string {
  const trimmed = value.trim();
  const date = trimmed.match(
    /\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{1,2})(?:,\s*\d{4})?/i,
  );
  if (date) return `${date[1].slice(0, 3)} ${Number(date[2])}`;
  return trimmed
    .replace(/^due:\s*/i, '')
    .replace(/^committed date\s+/i, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim();
}

function docketDueRange(items: ExecBriefDocketItem[]): string | null {
  const dates = Array.from(
    new Set(
      items
        .map((item) => compactDueLabel(item.dueLabel ?? ''))
        .filter(Boolean),
    ),
  );
  if (dates.length === 0) return null;
  if (dates.length === 1) return dates[0];
  const ordered = dates
    .map((label) => ({ label, sortKey: dueDateSortKey(label) }))
    .filter((entry) => entry.sortKey !== null)
    .sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));
  if (ordered.length !== dates.length) return null;
  return `${ordered[0].label} – ${ordered[ordered.length - 1].label}`;
}

function dueDateSortKey(value: string): number | null {
  const match = value.match(/^([A-Za-z]{3})\s+(\d{1,2})$/);
  if (!match) return null;
  const month = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ].indexOf(match[1].toLowerCase());
  return month < 0 ? null : month * 31 + Number(match[2]);
}

function movementBody(items: Array<ExecBriefMovementItem | BriefWatchGeneratedItem>): string {
  if (items.length === 0) return '';
  return `${items.length} change${items.length === 1 ? '' : 's'} since your last visit.`;
}

function watchValueAtStake(
  bindings: Map<string, ExecBriefV2Binding>,
  watch: BriefWatchGeneratedItem
): number {
  const binding = bindings.get(watch.valueAtStakeBindingId);
  return typeof binding?.numericValue === 'number' ? binding.numericValue : 0;
}

function watchItemBody(watch: BriefWatchGeneratedItem): string {
  return [
    'Leading-indicator watch fired',
    `Series: ${watch.governedSeries.label}`,
    `Threshold: ${watch.threshold.label}`,
    `Affects: ${watch.affected.label}`,
  ].join(' · ');
}

function watchItemDetail(watch: BriefWatchGeneratedItem): string {
  return [
    `${watch.governedSeries.label} moved from ${watch.governedSeries.movedFromLabel} to ${watch.governedSeries.movedToLabel}.`,
    watch.threshold.label,
    `Affected ${watch.affected.kind}: ${watch.affected.label}.`,
  ].join(' ');
}

function watchItemProvenance(watch: BriefWatchGeneratedItem): BriefSpecItemProvenance {
  const provenance: BriefSpecItemProvenance = {
    label: `Why am I seeing this: watch ${watch.provenance.watchId} fired from evidence ${watch.provenance.evidenceRefId}.`,
    watchId: watch.provenance.watchId,
    evidenceRefId: watch.provenance.evidenceRefId,
  };
  if (watch.provenance.evidenceLabel) provenance.evidenceLabel = watch.provenance.evidenceLabel;
  if (watch.provenance.firedAt) provenance.firedAt = watch.provenance.firedAt;
  if (watch.provenance.sourcePath) provenance.sourcePath = watch.provenance.sourcePath;
  return provenance;
}
